/**
 * LeadsWebhookCard — Clinic Settings → אינטגרציות card for Leads v2 inbound webhook
 * (leads_plan.md §8). Admin-only. All writes go through callables (leadsApi).
 *
 * States: plan blocked · off (no keys) · on (keys table, pause, counters, test, events, guide).
 * Keys are re-copyable by design (plaintext lives server-side, admin-only reveal).
 *
 * Error codes: CK-5900–5929.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Plugs,
  PlugsConnected,
  Pause,
  Copy,
  Eye,
  EyeSlash,
  Plus,
  ArrowsClockwise,
  Prohibit,
  PencilSimple,
  PaperPlaneTilt,
  CaretDown,
  WarningCircle,
  CheckCircle,
  Info,
  Code,
  Lightning,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectIsAdmin,
  selectPlanAllowsLeadsWebhook,
  selectIsTrialPlan,
} from "../../store/slices/authSlice";
import {
  useGetLeadsWebhookConfigQuery,
  useCreateLeadsApiKeyMutation,
  useUpdateLeadsApiKeyMutation,
  useRotateLeadsApiKeyMutation,
  useRevokeLeadsApiKeyMutation,
  useSetLeadsWebhookPausedMutation,
  useSendLeadsWebhookTestMutation,
} from "../../store/services/leadsApi";
import { useToast } from "../../contexts/ToastContext";
import ModalCloseButton from "../ui/ModalCloseButton";
import ConfirmDialog from "../ui/ConfirmDialog";
import LoaderLoop from "../shared/loader/LoaderLoop";
import ContentLoader from "../shared/ContentLoader";
import { formatDateTime } from "../../utils/dateUtils";
import { LEAD_FIELD_ALIASES, LEAD_INPUT_ERROR_LABELS } from "../../utils/leadContract";
import "./LeadsWebhookCard.css";

const OUTCOME_LABELS = {
  created: { label: "נקלט", tone: "ok" },
  rejected: { label: "נדחה", tone: "warn" },
  unauthorized: { label: "מפתח שגוי", tone: "danger" },
  rate_limited: { label: "חריגה מקצב", tone: "warn" },
  paused: { label: "מושהה", tone: "muted" },
  cap: { label: "מכסה חודשית", tone: "warn" },
};

const REJECT_REASONS = {
  "CK-10703": "שדה חסר או לא תקין",
  "CK-10704": "המסלול / ניסיון / השהיה",
  "CK-10705": "שגיאת שרת",
  "CK-10702": "חריגה מקצב",
  "CK-10709": "מכסה חודשית",
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

function KeyFormModal({ isOpen, mode, initial, isSaving, onClose, onSave }) {
  const [label, setLabel] = useState(initial?.label || "");
  const [source, setSource] = useState(initial?.defaultReferralSource || "");
  if (!isOpen) return null;
  return (
    <div className="lw-overlay" onClick={onClose}>
      <div className="lw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lw-modal-header">
          <div className="lw-modal-title">
            <span className="lw-icon-tile"><Plugs size={18} weight="fill" /></span>
            <div>
              <span className="lw-eyebrow">קליטת מתעניינים</span>
              <h3>{mode === "edit" ? "עריכת מפתח" : "מפתח חדש"}</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div className="lw-modal-body">
          <label className="lw-field">
            <span>שם המפתח (איפה הוא משמש)</span>
            <input type="text" value={label} maxLength={40} placeholder="לדוגמה: אתר, פייסבוק, Zapier" onChange={(e) => setLabel(e.target.value)} autoFocus />
          </label>
          <label className="lw-field">
            <span>מקור הפניה ברירת מחדל</span>
            <input type="text" value={source} maxLength={80} placeholder="אם הטופס לא שולח מקור — ישמר זה" onChange={(e) => setSource(e.target.value)} />
          </label>
        </div>
        <div className="lw-modal-footer">
          <button type="button" className="lw-btn secondary" onClick={onClose} disabled={isSaving}>ביטול</button>
          <button type="button" className="lw-btn primary" disabled={isSaving || !label.trim()} onClick={() => onSave({ label: label.trim(), defaultReferralSource: source.trim() || null })}>
            {isSaving && <LoaderLoop size="xs" />}
            <span>{mode === "edit" ? "שמור" : "צור מפתח"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function KeyRow({ k, onCopy, onEdit, onRotate, onRevoke, onTest, busy }) {
  const [revealed, setRevealed] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  // Presentation-only: close the ⋯ menu on Escape / outside click (mouse + touch)
  useEffect(() => {
    if (!menu) return undefined;
    const onPointer = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenu(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);
  const revoked = k.status === "revoked";
  return (
    <div className={`lw-key-row${revoked ? " is-revoked" : ""}`}>
      <div className="lw-key-main">
        <div className="lw-key-title">
          <span className="lw-key-label">{k.label || "ללא שם"}</span>
          {revoked ? <span className="lw-badge muted">בוטל</span> : <span className="lw-badge ok">פעיל</span>}
        </div>
        <div className="lw-key-value">
          <code dir="ltr">{revealed && k.plaintext ? k.plaintext : k.masked}</code>
          {!revoked && k.plaintext && (
            <>
              <button type="button" className="lw-icon-btn" onClick={() => setRevealed((v) => !v)} title={revealed ? "הסתר" : "הצג"} aria-label={revealed ? "הסתר מפתח" : "הצג מפתח"}>
                {revealed ? <EyeSlash size={15} /> : <Eye size={15} />}
              </button>
              <button type="button" className="lw-icon-btn" onClick={() => onCopy(k.plaintext)} title="העתק מפתח" aria-label="העתק מפתח">
                <Copy size={15} />
              </button>
            </>
          )}
        </div>
        <div className="lw-key-meta">
          <span>מקור: {k.defaultReferralSource || k.label || "—"}</span>
          <span className="lw-sep">·</span>
          <span>{k.useCount || 0} קליטות</span>
          {k.lastUsedAt && (
            <>
              <span className="lw-sep">·</span>
              <span>אחרון: {formatDateTime(k.lastUsedAt)}</span>
            </>
          )}
        </div>
      </div>
      {!revoked && (
        <div className="lw-key-actions">
          <button type="button" className="lw-btn ghost sm" onClick={() => onTest(k)} disabled={busy} title="שלח מתעניין בדיקה דרך מפתח זה">
            <PaperPlaneTilt size={14} /> בדיקה
          </button>
          <div className="lw-menu-wrap" ref={menuRef}>
            <button type="button" className="lw-icon-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((v) => !v)} title="פעולות" aria-label="פעולות על המפתח">
              <CaretDown size={14} weight="bold" />
            </button>
            {menu && (
              <div className="lw-menu" role="menu" aria-label="פעולות על המפתח">
                <button type="button" role="menuitem" onClick={() => { setMenu(false); onEdit(k); }}><PencilSimple size={14} /> ערוך</button>
                <button type="button" role="menuitem" onClick={() => { setMenu(false); onRotate(k); }}><ArrowsClockwise size={14} /> החלף מפתח</button>
                <button type="button" role="menuitem" className="danger" onClick={() => { setMenu(false); onRevoke(k); }}><Prohibit size={14} /> בטל מפתח</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GuideAccordion({ endpoint, sampleKey }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("curl");
  const key = sampleKey || "ck_live_XXXXXXXXXXXX_YYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYY";
  const curl = `curl -X POST "${endpoint}" \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"דנה כהן","phone":"050-1234567","email":"dana@example.com","source":"פייסבוק","notes":"מתעניינת בטיפול"}'`;
  const noHeader = `${endpoint}?key=${key}`;
  return (
    <section className="lw-guide">
      <button type="button" className="lw-guide-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Code size={16} weight="bold" />
        <span>איך מחברים? (Zapier, Make, Elementor, Wix, פייסבוק, CRM)</span>
        <CaretDown size={14} weight="bold" className={`lw-caret${open ? " is-open" : ""}`} />
      </button>
      {open && (
        <div className="lw-guide-body">
          <p>שולחים בקשת <code>POST</code> לכתובת למטה עם המפתח, ובגוף הבקשה את פרטי המתעניין (JSON או טופס רגיל). המתעניין יופיע מיד בלשונית מתעניינים.</p>
          <div className="lw-guide-tabs" role="tablist">
            {[["curl", "cURL / קוד"], ["zapier", "Zapier / Make"], ["forms", "Elementor / Wix"], ["fb", "פייסבוק לידים"], ["fields", "שדות ותגובות"]].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} className={`lw-guide-tab${tab === id ? " active" : ""}`} onClick={() => setTab(id)}>{label}</button>
            ))}
          </div>
          {tab === "curl" && (
            <pre dir="ltr" className="lw-code"><code>{curl}</code></pre>
          )}
          {tab === "zapier" && (
            <ol className="lw-steps">
              <li>הוסיפו פעולה <strong>Webhooks by Zapier → POST</strong> (ב-Make: מודול <strong>HTTP → Make a request</strong>).</li>
              <li>URL: <code dir="ltr">{endpoint}</code></li>
              <li>Payload Type: <strong>json</strong>. Headers: <code dir="ltr">Authorization: Bearer {key}</code></li>
              <li>Data: <code>name</code>, <code>phone</code>, <code>email</code>, <code>source</code>, <code>notes</code> — ממפים לשדות של הטופס/ה-CRM.</li>
              <li>הפעילו "בדיקה" כאן למטה ובדקו שהמתעניין הופיע בלשונית מתעניינים.</li>
            </ol>
          )}
          {tab === "forms" && (
            <div className="lw-steps-wrap">
              <p>בוני אתרים ששולחים Webhook אבל לא מאפשרים Headers — משתמשים בכתובת עם המפתח בתוכה:</p>
              <pre dir="ltr" className="lw-code"><code>{noHeader}</code></pre>
              <ol className="lw-steps">
                <li><strong>Elementor:</strong> Form → Actions After Submit → Webhook → הדביקו את הכתובת. שמות השדות בטופס: name, phone, email, notes.</li>
                <li><strong>Wix Forms:</strong> Automations → "Send via Webhook" → הכתובת למעלה. השדות ממופים אוטומטית לפי השם (גם בעברית: שם, טלפון, אימייל, הודעה).</li>
                <li>שדות שלא מוכרים נזרקים ולא נשמרים.</li>
              </ol>
            </div>
          )}
          {tab === "fb" && (
            <ol className="lw-steps">
              <li>פייסבוק לידים לא שולח Webhook ישירות לכתובת חיצונית — מחברים דרך Zapier / Make: Trigger <strong>Facebook Lead Ads → New Lead</strong>.</li>
              <li>Action: Webhook POST לכתובת כאן עם המפתח (ראו לשונית Zapier / Make).</li>
              <li>ממפים: full_name → name, phone_number → phone, email → email, ושם הקמפיין → source.</li>
            </ol>
          )}
          {tab === "fields" && (
            <div className="lw-fields">
              <div className="lw-table-wrap">
              <table className="lw-table" dir="rtl">
                <thead><tr><th>שדה</th><th>שמות מקובלים</th><th>חובה</th></tr></thead>
                <tbody>
                  <tr><td>שם</td><td dir="ltr">{LEAD_FIELD_ALIASES.name.join(", ")}</td><td>כן</td></tr>
                  <tr><td>טלפון</td><td dir="ltr">{LEAD_FIELD_ALIASES.phone.join(", ")}</td><td>טלפון או אימייל</td></tr>
                  <tr><td>אימייל</td><td dir="ltr">{LEAD_FIELD_ALIASES.email.join(", ")}</td><td>טלפון או אימייל</td></tr>
                  <tr><td>מקור / קמפיין</td><td dir="ltr">{LEAD_FIELD_ALIASES.referralSource.join(", ")}, utm_*</td><td>לא</td></tr>
                  <tr><td>הערות</td><td dir="ltr">{LEAD_FIELD_ALIASES.notes.join(", ")}</td><td>לא</td></tr>
                </tbody>
              </table>
              </div>
              <div className="lw-table-wrap">
              <table className="lw-table" dir="rtl">
                <thead><tr><th>תגובה</th><th>משמעות</th></tr></thead>
                <tbody>
                  <tr><td dir="ltr">201 {"{ ok: true, leadId }"}</td><td>נקלט. <code>duplicateOfPhone</code> = קיים מתעניין עם אותו טלפון</td></tr>
                  <tr><td dir="ltr">400 missing_name / missing_contact / invalid_phone / invalid_email</td><td>{LEAD_INPUT_ERROR_LABELS.missing_contact} / {LEAD_INPUT_ERROR_LABELS.invalid_phone}</td></tr>
                  <tr><td dir="ltr">401 unauthorized</td><td>מפתח חסר, שגוי או מבוטל</td></tr>
                  <tr><td dir="ltr">403 paused / trial_expired / plan</td><td>הקליטה מושהית / תקופת הניסיון הסתיימה / המסלול לא כולל</td></tr>
                  <tr><td dir="ltr">429 rate_limited / monthly_cap</td><td>יותר מ-60 בשעה למפתח / מכסה חודשית (ניסיון: 50)</td></tr>
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function LeadsWebhookCard() {
  const clinicId = useSelector(selectClinicId);
  const isAdmin = useSelector(selectIsAdmin);
  const planAllows = useSelector(selectPlanAllowsLeadsWebhook);
  const isTrial = useSelector(selectIsTrialPlan);
  const { showSuccess, showError } = useToast();

  const { data: config, isLoading, isError } = useGetLeadsWebhookConfigQuery({ clinicId }, { skip: !clinicId || !isAdmin });
  const [createKey, { isLoading: isCreating }] = useCreateLeadsApiKeyMutation();
  const [updateKey, { isLoading: isUpdating }] = useUpdateLeadsApiKeyMutation();
  const [rotateKey, { isLoading: isRotating }] = useRotateLeadsApiKeyMutation();
  const [revokeKey, { isLoading: isRevoking }] = useRevokeLeadsApiKeyMutation();
  const [setPaused, { isLoading: isPausing }] = useSetLeadsWebhookPausedMutation();
  const [sendTest, { isLoading: isTesting }] = useSendLeadsWebhookTestMutation();
  const busy = isCreating || isUpdating || isRotating || isRevoking || isPausing || isTesting;

  const [keyForm, setKeyForm] = useState(null); // { mode, key }
  const [confirm, setConfirm] = useState({ isOpen: false });
  const [testResult, setTestResult] = useState(null);

  const activeKeys = useMemo(() => (config?.keys || []).filter((k) => k.status === "active"), [config]);
  const enabled = activeKeys.length > 0;
  const endpoint = config?.endpoint || "https://clinikit.co.il/api/leads/inbound";

  if (!isAdmin) return null;

  const handleCopy = async (text, what = "המפתח") => {
    if (await copyText(text)) showSuccess(`${what} הועתק`);
    else showError("ההעתקה נכשלה — העתיקו ידנית");
  };

  const handleCreate = async (payload) => {
    try {
      await createKey({ clinicId, ...payload }).unwrap();
      setKeyForm(null);
      showSuccess("המפתח נוצר. ניתן להעתיק אותו מהטבלה בכל עת.");
    } catch (error) {
      console.error("[CK-5900] LeadsWebhookCard: create key failed", { errorCode: "CK-5900", errorMessage: error?.message, clinicId, timestamp: new Date().toISOString() });
      showError(error?.message || "יצירת המפתח נכשלה");
    }
  };
  const handleUpdate = async (payload) => {
    try {
      await updateKey({ clinicId, keyId: keyForm.key.keyId, ...payload }).unwrap();
      setKeyForm(null);
      showSuccess("המפתח עודכן");
    } catch (error) {
      console.error("[CK-5901] LeadsWebhookCard: update key failed", { errorCode: "CK-5901", errorMessage: error?.message, clinicId, timestamp: new Date().toISOString() });
      showError(error?.message || "עדכון המפתח נכשל");
    }
  };
  const handleRotate = (k) =>
    setConfirm({
      isOpen: true,
      type: "warning",
      icon: "warning",
      title: "החלפת מפתח",
      message: `להחליף את המפתח "${k.label}"? המפתח הישן יפסיק לעבוד מיידית — יש לעדכן אותו בכל מקום שבו הוא מוגדר.`,
      confirmText: "החלף",
      onConfirm: async () => {
        try {
          await rotateKey({ clinicId, keyId: k.keyId }).unwrap();
          showSuccess("המפתח הוחלף. העתיקו את המפתח החדש מהטבלה.");
        } catch (error) {
          console.error("[CK-5902] LeadsWebhookCard: rotate key failed", { errorCode: "CK-5902", errorMessage: error?.message, clinicId, timestamp: new Date().toISOString() });
          showError(error?.message || "החלפת המפתח נכשלה");
        }
      },
    });
  const handleRevoke = (k) =>
    setConfirm({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "ביטול מפתח",
      message: `לבטל את המפתח "${k.label}"? בקשות עם מפתח זה יידחו מעכשיו.`,
      confirmText: "בטל מפתח",
      onConfirm: async () => {
        try {
          await revokeKey({ clinicId, keyId: k.keyId }).unwrap();
          showSuccess("המפתח בוטל");
        } catch (error) {
          console.error("[CK-5903] LeadsWebhookCard: revoke key failed", { errorCode: "CK-5903", errorMessage: error?.message, clinicId, timestamp: new Date().toISOString() });
          showError(error?.message || "ביטול המפתח נכשל");
        }
      },
    });
  const handlePause = async (paused) => {
    try {
      await setPaused({ clinicId, paused }).unwrap();
      showSuccess(paused ? "הקליטה הושהתה" : "הקליטה חודשה");
    } catch (error) {
      console.error("[CK-5904] LeadsWebhookCard: pause toggle failed", { errorCode: "CK-5904", errorMessage: error?.message, clinicId, timestamp: new Date().toISOString() });
      showError(error?.message || "עדכון ההשהיה נכשל");
    }
  };
  const handleTest = async (k) => {
    setTestResult(null);
    try {
      const res = await sendTest({ clinicId, keyId: k.keyId }).unwrap();
      setTestResult(res);
      if (res?.ok) showSuccess("מתעניין הבדיקה נקלט — בדקו בלשונית מתעניינים");
      else showError("הבדיקה חזרה עם שגיאה — ראו פירוט בכרטיס");
    } catch (error) {
      console.error("[CK-5905] LeadsWebhookCard: test send failed", { errorCode: "CK-5905", errorMessage: error?.message, clinicId, timestamp: new Date().toISOString() });
      showError(error?.message || "שליחת הבדיקה נכשלה");
    }
  };

  return (
    <>
      <section className="lw-card" aria-labelledby="lw-card-title">
        <header className="lw-card-head">
          <span className="lw-icon-tile"><Lightning size={22} weight="fill" /></span>
          <div className="lw-card-head-text">
            <div className="lw-card-title-row">
              <h3 id="lw-card-title">קליטת מתעניינים אוטומטית</h3>
              {!planAllows ? (
                <span className="lw-badge muted"><Plugs size={13} weight="bold" />לא כלול במסלול</span>
              ) : config?.paused ? (
                <span className="lw-badge warn"><Pause size={13} weight="bold" />מושהה</span>
              ) : enabled ? (
                <span className="lw-badge ok"><PlugsConnected size={13} weight="bold" />פעיל</span>
              ) : (
                <span className="lw-badge muted"><Plugs size={13} weight="bold" />לא הופעל</span>
              )}
            </div>
            <p>חיבור טפסים באתר, קמפיינים ו-CRM ישירות ללשונית מתעניינים. כל מערכת מקבלת מפתח משלה — אפשר לבטל אחד בלי לפגוע באחרים.</p>
          </div>
        </header>

        <div className="lw-card-body">
          {!planAllows ? (
            <div className="lw-callout warn">
              <WarningCircle size={18} weight="fill" />
              <span>קליטת מתעניינים אוטומטית אינה כלולה במסלול הנוכחי. פנו אלינו לשדרוג.</span>
            </div>
          ) : isLoading ? (
            <ContentLoader message="טוען הגדרות קליטה..." />
          ) : isError ? (
            <div className="lw-callout warn">
              <WarningCircle size={18} weight="fill" />
              <span>טעינת הגדרות הקליטה נכשלה. נסו לרענן את הדף.</span>
            </div>
          ) : !enabled ? (
            <>
              <ul className="lw-benefits">
                <li><CheckCircle size={16} weight="fill" />מתעניין שממלא טופס באתר מופיע אצלכם תוך שניות — בלי הקלדה</li>
                <li><CheckCircle size={16} weight="fill" />עובד עם Zapier, Make, Elementor, Wix, פייסבוק לידים וכל CRM ששולח Webhook</li>
                <li><CheckCircle size={16} weight="fill" />מפתח נפרד לכל מקור, מקור הפניה אוטומטי והתראה על כל מתעניין חדש</li>
              </ul>
              {isTrial && (
                <div className="lw-callout">
                  <Info size={18} weight="fill" />
                  <span>בתקופת הניסיון ניתן לקלוט עד {config?.counters?.monthlyCap ?? 50} מתעניינים בחודש.</span>
                </div>
              )}
              <div className="lw-card-actions">
                <button type="button" className="lw-btn primary" onClick={() => setKeyForm({ mode: "create", key: null })} disabled={busy}>
                  <Plugs size={16} weight="bold" /> הפעל קליטה
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="lw-endpoint">
                <span className="lw-field-label">כתובת לשליחת מתעניינים (POST)</span>
                <div className="lw-endpoint-row">
                  <code dir="ltr">{endpoint}</code>
                  <button type="button" className="lw-icon-btn" onClick={() => handleCopy(endpoint, "הכתובת")} title="העתק כתובת" aria-label="העתק כתובת">
                    <Copy size={15} />
                  </button>
                </div>
              </div>

              <div className="lw-stats">
                <div className="lw-stat"><span className="lw-stat-value">{config?.counters?.today ?? 0}</span><span className="lw-stat-label">נקלטו היום</span></div>
                <div className="lw-stat">
                  <span className="lw-stat-value">{config?.counters?.month ?? 0}{config?.counters?.monthlyCap ? <span className="lw-stat-cap"> / {config.counters.monthlyCap}</span> : null}</span>
                  <span className="lw-stat-label">נקלטו החודש</span>
                </div>
                <div className="lw-stat"><span className="lw-stat-value">{activeKeys.length}</span><span className="lw-stat-label">מפתחות פעילים</span></div>
                <label className="lw-toggle-row">
                  <span className="lw-toggle-text">
                    <span className="lw-toggle-label">השהה קליטה</span>
                    <span className="lw-toggle-hint">בקשות יידחו עם 403 עד לחידוש</span>
                  </span>
                  <span className="lw-toggle">
                    <input type="checkbox" checked={config?.paused === true} disabled={busy} onChange={(e) => handlePause(e.target.checked)} aria-label="השהה קליטה" />
                    <span className="lw-toggle-slider" />
                  </span>
                </label>
              </div>

              <div className="lw-keys">
                <div className="lw-section-head">
                  <h4>מפתחות</h4>
                  <button type="button" className="lw-btn secondary sm" onClick={() => setKeyForm({ mode: "create", key: null })} disabled={busy || activeKeys.length >= 10}>
                    <Plus size={14} weight="bold" /> מפתח חדש
                  </button>
                </div>
                {(config?.keys || []).map((k) => (
                  <KeyRow key={k.keyId} k={k} busy={busy} onCopy={handleCopy} onEdit={(key) => setKeyForm({ mode: "edit", key })} onRotate={handleRotate} onRevoke={handleRevoke} onTest={handleTest} />
                ))}
              </div>

              {testResult && (
                <div className={`lw-callout${testResult.ok ? "" : " warn"}`}>
                  {testResult.ok ? <CheckCircle size={18} weight="fill" /> : <WarningCircle size={18} weight="fill" />}
                  <span>
                    תוצאת הבדיקה: <code dir="ltr">{testResult.status}</code>
                    {testResult.response?.leadId ? ` · נוצר מתעניין "מתעניין בדיקה"` : testResult.response?.error ? ` · ${testResult.response.error}` : ""}
                  </span>
                </div>
              )}

              {(config?.events || []).length > 0 && (
                <div className="lw-events">
                  <div className="lw-section-head"><h4>קליטות אחרונות</h4></div>
                  <div className="lw-table-wrap">
                    <table className="lw-table">
                      <thead><tr><th>זמן</th><th>מפתח</th><th>תוצאה</th><th>שם</th></tr></thead>
                      <tbody>
                        {config.events.map((e) => {
                          const o = OUTCOME_LABELS[e.outcome] || { label: e.outcome, tone: "muted" };
                          return (
                            <tr key={e.id}>
                              <td>{e.at ? formatDateTime(e.at) : "—"}</td>
                              <td>{e.keyLabel || "—"}</td>
                              <td><span className={`lw-badge ${o.tone}`}>{o.label}{e.outcome === "rejected" && REJECT_REASONS[e.errorCode] ? ` · ${REJECT_REASONS[e.errorCode]}` : ""}</span></td>
                              <td>{e.leadName || "—"}{e.test ? " (בדיקה)" : ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <GuideAccordion endpoint={endpoint} sampleKey={activeKeys[0]?.plaintext || null} />
            </>
          )}
        </div>
      </section>

      <KeyFormModal
        key={keyForm ? `${keyForm.mode}-${keyForm.key?.keyId || "new"}` : "closed"}
        isOpen={!!keyForm}
        mode={keyForm?.mode}
        initial={keyForm?.key}
        isSaving={isCreating || isUpdating}
        onClose={() => setKeyForm(null)}
        onSave={keyForm?.mode === "edit" ? handleUpdate : handleCreate}
      />

      <ConfirmDialog
        isOpen={confirm.isOpen}
        onClose={() => setConfirm((c) => ({ ...c, isOpen: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        icon={confirm.icon}
        confirmText={confirm.confirmText || "אישור"}
      />
    </>
  );
}
